import React, { createContext, useContext } from 'react';
import { AuthServiceConfig, Security } from '@vis-auth/react';
import { EveRoutes } from '@ver-uds/e-verify';
import { useEnvironment } from '../Environment/EnvironmentContext';
import AuthScopes from '../../constants/AuthScopes';
import { MouRoutes } from '../../components/ClientMou/constants';

export const SecurityContext = createContext<AuthServiceConfig | null>(null);

export const useSecurityContext = (): AuthServiceConfig | null => {
  const authConfig = useContext(SecurityContext);

  if (!authConfig) {
    throw new Error('useSecurityContext must be used inside SecurityProvider');
  }

  return authConfig;
};

export type SecurityProviderProps = {
  children: React.ReactNode;
};

const SecurityProvider = ({ children }: SecurityProviderProps): JSX.Element => {
  const {
    AUTH_URL,
    AUTH_CLIENTID,
    AUTH_REDIRECT,
    LOGIN_DOT_GOV_AUTH_CLIENTID,
    LOGIN_DOT_GOV_AUTH_REDIRECT,
    LOGIN_DOT_GOV_URL,
  } = useEnvironment();
  const SESSION_WARNING_TIMER = 20 * 60 * 1000; // The number of ms of inactivity before state is set to `sessionWarning`.
  const SESSION_TIMEOUT_TIMER = 5 * 60; // The number of seconds AFTER the warning time before being logged out.

  const isLoginDotGov = LOGIN_DOT_GOV_AUTH_CLIENTID && window.location.pathname.includes('loginDotGov');

  // Used to determine fallback route when Session times out for OTC authenticated enrollment
  const isOTCEnrollment =
    window.location.pathname.includes('account/enroll') && !window.location.pathname.includes(MouRoutes.BASE);

  const authConfig: AuthServiceConfig = {
    issuer: AUTH_URL,
    clientId: isLoginDotGov ? LOGIN_DOT_GOV_AUTH_CLIENTID : AUTH_CLIENTID,
    // The identity service URL is configured by the gateway proxy, so we use location.origin here.
    redirectUri: `${window.location.origin}${isLoginDotGov ? LOGIN_DOT_GOV_AUTH_REDIRECT : AUTH_REDIRECT}`,
    logoutUri: isOTCEnrollment ? `${window.location.origin}${EveRoutes.ACCOUNT_ENROLL}` : undefined,
    loginDotGovUrl: LOGIN_DOT_GOV_URL,
    tokenStorage: 'cookie',
    errorPath: '/error',
    session: {
      // this warning and timeout state is currently not being used but instead handled by SessionHandler component,
      // including here because they are required properties in order to set disableNavigationRefresh
      warning: SESSION_WARNING_TIMER,
      timeout: SESSION_TIMEOUT_TIMER,
      disableNavigationRefresh: true,
    },
    scope: isLoginDotGov ? AuthScopes.LOGIN_DOT_GOV : AuthScopes.VIS,
  };

  return (
    <>
      <SecurityContext.Provider value={authConfig}>
        <Security authConfig={authConfig}>{children}</Security>
      </SecurityContext.Provider>
    </>
  );
};

export default SecurityProvider;
